/**
 * Persists the last-opened PDF for HomePage restore (manual workflow only).
 * localStorage cannot hold large PDFs (quota); IndexedDB uses a much larger per-origin budget.
 * Local-server and Cloud DB flows must not write here — PDFs are fetched on demand.
 */

const DB_NAME = 'QualityDatumSavedPdf';
const DB_VERSION = 1;
const STORE_NAME = 'savedPdf';
const RECORD_KEY = 'lastPdf';

const LEGACY_LS_KEY = 'savedPdfFile';
const LEGACY_LS_NAME_KEY = 'savedPdfFileName';

type SavedPdfRecord = {
    fileName: string;
    blob: Blob;
};

function openSavedPdfDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'));
        request.onsuccess = () => resolve(request.result);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME);
            }
        };
    });
}

function clearLegacyLocalStorage(): void {
    try {
        localStorage.removeItem(LEGACY_LS_KEY);
        localStorage.removeItem(LEGACY_LS_NAME_KEY);
    } catch {
        // ignore
    }
}

/**
 * Stores PDF bytes for restore on the home screen. Clears legacy localStorage keys on success.
 */
export async function storeSavedPdf(file: File): Promise<void> {
    const db = await openSavedPdfDb();
    const record: SavedPdfRecord = { fileName: file.name, blob: file };

    await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
        tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
        tx.objectStore(STORE_NAME).put(record, RECORD_KEY);
    });
    db.close();
    clearLegacyLocalStorage();
}

/**
 * Loads the last stored PDF, or migrates a legacy data-URL entry from localStorage once.
 */
export async function loadSavedPdf(): Promise<File | null> {
    try {
        const db = await openSavedPdfDb();
        const record = await new Promise<SavedPdfRecord | undefined>((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const request = tx.objectStore(STORE_NAME).get(RECORD_KEY);
            request.onerror = () => reject(request.error ?? new Error('IndexedDB get failed'));
            request.onsuccess = () => resolve(request.result as SavedPdfRecord | undefined);
        });
        db.close();

        if (record?.blob) {
            const name = record.fileName || 'saved.pdf';
            return new File([ record.blob ], name, { type: 'application/pdf' });
        }
    } catch {
        // fall through to legacy
    }

    const legacy = (() => {
        try {
            return localStorage.getItem(LEGACY_LS_KEY);
        } catch {
            return null;
        }
    })();

    if (!legacy) {
        return null;
    }

    try {
        const response = await fetch(legacy);
        const blob = await response.blob();
        const name = (() => {
            try {
                return localStorage.getItem(LEGACY_LS_NAME_KEY) || 'saved.pdf';
            } catch {
                return 'saved.pdf';
            }
        })();
        const file = new File([ blob ], name, { type: 'application/pdf' });
        try {
            await storeSavedPdf(file);
        } catch {
            // If IDB save fails, keep legacy keys so a smaller save might work later
            return file;
        }
        return file;
    } catch {
        clearLegacyLocalStorage();
        return null;
    }
}

/** Clears last-opened PDF used for HomePage / viewer restore after explicit user exit. */
export async function clearSavedPdf(): Promise<void> {
    try {
        const db = await openSavedPdfDb();
        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error ?? new Error('IndexedDB delete failed'));
            tx.onabort = () => reject(tx.error ?? new Error('IndexedDB delete aborted'));
            tx.objectStore(STORE_NAME).delete(RECORD_KEY);
        });
        db.close();
    } catch {
        // ignore
    }
    clearLegacyLocalStorage();
}
